import React, { useState, useEffect, useMemo } from 'react';
import { SchoolClass, Student } from '../types';
import { useAcademicYear } from '../context/AcademicYearContext';
import { getCachedStudents, dataCache } from '../services/dataCacheService';
import { bulkEnrollStudentsInYear, getStudentClassForYear } from '../services/academicYearService';
import { 
  X, 
  Search, 
  UserPlus, 
  Check, 
  Loader2, 
  Filter, 
  CheckSquare, 
  Square,
  Users,
  Sparkles,
  ArrowRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  targetClass: SchoolClass | null;
  onSuccess?: () => void;
}

export const EnrollStudentsModal: React.FC<Props> = ({
  isOpen,
  onClose,
  targetClass,
  onSuccess
}) => {
  const { selectedYear, currentYearObj } = useAcademicYear();
  const [students, setStudents] = useState<Student[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [filterMode, setFilterMode] = useState<'unenrolled' | 'all' | 'prevClass'>('unenrolled');
  const [selectedPrevClass, setSelectedPrevClass] = useState<string>('all');
  const [submitting, setSubmitting] = useState(false);

  // Load all students from repository
  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    const fetchStudents = async () => {
      try {
        const list = await getCachedStudents();
        setStudents(list);
      } catch (err) {
        console.error('Error fetching students repository:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchStudents();
  }, [isOpen]);

  // Extract list of unique previous classes (from 2025-2026)
  const previousClasses = useMemo(() => {
    const classSet = new Set<string>();
    students.forEach(s => {
      const prev = getStudentClassForYear(s, '2025-2026');
      if (prev?.className) {
        classSet.add(prev.className);
      }
    });
    return Array.from(classSet).sort((a, b) => a.localeCompare(b, 'he'));
  }, [students]);

  // Filter students based on filterMode, selectedPrevClass, and search
  const filteredStudents = useMemo(() => {
    return students.filter(s => {
      // Check current year enrollment
      const enrolledCurrent = getStudentClassForYear(s, selectedYear);
      const isAlreadyInTargetClass = enrolledCurrent?.classId === targetClass?.id;

      if (filterMode === 'unenrolled') {
        // Show only students not yet enrolled in any class for selectedYear
        if (enrolledCurrent !== null) return false;
      } else if (filterMode === 'prevClass') {
        if (selectedPrevClass !== 'all') {
          const prev = getStudentClassForYear(s, '2025-2026');
          if (prev?.className !== selectedPrevClass) return false;
        }
      }

      // Search term
      if (searchTerm.trim()) {
        const query = searchTerm.toLowerCase();
        const matchesName = (s.fullName || '').toLowerCase().includes(query);
        const matchesId = (s.nationalId || '').includes(query);
        const matchesPrevClass = (s.className || '').toLowerCase().includes(query);
        if (!matchesName && !matchesId && !matchesPrevClass) return false;
      }

      return true;
    });
  }, [students, filterMode, selectedPrevClass, searchTerm, selectedYear, targetClass]);

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllFiltered = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      filteredStudents.forEach(s => next.add(s.id));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const handleEnroll = async () => {
    if (!targetClass || selectedIds.size === 0) return;
    setSubmitting(true);
    try {
      const count = await bulkEnrollStudentsInYear(
        Array.from(selectedIds),
        selectedYear,
        targetClass.id,
        targetClass.name
      );
      dataCache.remove('students');
      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to enroll students:', err);
      alert('שגיאה בשיבוץ התלמידים לכיתה');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen || !targetClass) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-xs" dir="rtl">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-100 flex flex-col max-h-[90vh] overflow-hidden"
        >
          {/* Header */}
          <div className="px-6 py-5 bg-gradient-to-r from-emerald-600 to-teal-700 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-white/10 rounded-2xl backdrop-blur-xs">
                <Users size={24} className="text-emerald-100" />
              </div>
              <div>
                <h3 className="text-lg font-black tracking-tight">שיבוץ תלמידים ממאגר בית הספר</h3>
                <p className="text-xs text-emerald-100 mt-0.5">
                  כיתת יעד: <span className="font-bold underline">{targetClass.name}</span> • שנת לימודים: {currentYearObj.name}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              disabled={submitting}
              className="p-2 text-white/70 hover:text-white hover:bg-white/10 rounded-xl transition-all"
            >
              <X size={20} />
            </button>
          </div>

          {/* Filters Bar */}
          <div className="p-4 bg-slate-50 border-b border-slate-100 space-y-3">
            {/* Filter mode buttons */}
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={() => setFilterMode('unenrolled')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  filterMode === 'unenrolled'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <Sparkles size={14} />
                <span>טרם שובצו לשנת {currentYearObj.id}</span>
              </button>

              <button
                type="button"
                onClick={() => setFilterMode('prevClass')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 ${
                  filterMode === 'prevClass'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                <Filter size={14} />
                <span>לפי כיתה בשנה הקודמת</span>
              </button>

              <button
                type="button"
                onClick={() => setFilterMode('all')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
                  filterMode === 'all'
                    ? 'bg-emerald-600 text-white shadow-xs'
                    : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
                }`}
              >
                כל המאגר ({students.length})
              </button>
            </div>

            {/* If prevClass selected, dropdown of past classes */}
            {filterMode === 'prevClass' && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-slate-600">בחר כיתת מקור:</span>
                <select
                  value={selectedPrevClass}
                  onChange={(e) => setSelectedPrevClass(e.target.value)}
                  className="px-3 py-1.5 bg-white border border-slate-200 rounded-xl text-xs font-medium outline-none focus:ring-2 focus:ring-emerald-500"
                >
                  <option value="all">כל הכיתות מהשנה הקודמת</option>
                  {previousClasses.map(name => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Search and Bulk Select */}
            <div className="flex items-center justify-between gap-3">
              <div className="relative flex-1">
                <Search size={16} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="חיפוש תלמיד לפי שם או ת.ז..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-3 pr-9 py-2 bg-white rounded-xl border border-slate-200 text-xs outline-none focus:ring-2 focus:ring-emerald-500"
                />
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAllFiltered}
                  className="px-3 py-2 bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 rounded-xl text-xs font-bold transition-all"
                >
                  בחר הכל ({filteredStudents.length})
                </button>
                {selectedIds.size > 0 && (
                  <button
                    type="button"
                    onClick={clearSelection}
                    className="px-3 py-2 text-rose-600 hover:bg-rose-50 rounded-xl text-xs font-bold transition-all"
                  >
                    נקה בחירה
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Student List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-1.5 divide-y divide-slate-100">
            {loading ? (
              <div className="p-16 flex flex-col items-center justify-center text-slate-400">
                <Loader2 className="animate-spin mb-3" size={32} />
                <p className="text-xs font-bold">טוען מאגר תלמידים...</p>
              </div>
            ) : filteredStudents.length === 0 ? (
              <div className="p-12 text-center text-slate-400">
                <Users className="mx-auto mb-2 opacity-40" size={36} />
                <p className="text-sm font-bold">לא נמצאו תלמידים מתאימים</p>
                <p className="text-xs text-slate-400 mt-1">נסה לשנות את הסינון או את החיפוש</p>
              </div>
            ) : (
              filteredStudents.map(student => {
                const isSelected = selectedIds.has(student.id);
                const currentEnrollment = getStudentClassForYear(student, selectedYear);
                const prevEnrollment = getStudentClassForYear(student, '2025-2026');

                return (
                  <div
                    key={student.id}
                    onClick={() => toggleSelect(student.id)}
                    className={`pt-2 pb-2 px-3 rounded-2xl flex items-center justify-between cursor-pointer transition-all ${
                      isSelected ? 'bg-emerald-50 border border-emerald-200' : 'hover:bg-slate-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`p-1 rounded-lg ${isSelected ? 'text-emerald-600' : 'text-slate-300'}`}>
                        {isSelected ? <CheckSquare size={20} /> : <Square size={20} />}
                      </div>

                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-bold text-slate-800">{student.fullName}</span>
                          {student.nationalId && (
                            <span className="text-[11px] text-slate-400 font-mono">ת.ז: {student.nationalId}</span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-0.5">
                          {prevEnrollment && (
                            <span>שנה קודמת: <strong className="text-slate-700">{prevEnrollment.className}</strong></span>
                          )}
                          {currentEnrollment && (
                            <span className="text-indigo-600 font-bold bg-indigo-50 px-1.5 py-0.5 rounded-md">
                              משובץ כבר ל: {currentEnrollment.className}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="text-xs">
                      {isSelected ? (
                        <span className="px-2.5 py-1 bg-emerald-600 text-white rounded-lg font-bold text-[11px]">
                          נבחר
                        </span>
                      ) : (
                        <span className="text-slate-400 text-[11px]">לחץ לבחירה</span>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer Bar */}
          <div className="p-4 bg-slate-50 border-t border-slate-100 flex items-center justify-between">
            <div>
              <span className="text-xs text-slate-500">נבחרו לשיבוץ: </span>
              <strong className="text-sm text-emerald-700 font-black">{selectedIds.size} תלמידים</strong>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:bg-slate-200 rounded-xl transition-all"
              >
                ביטול
              </button>

              <button
                type="button"
                onClick={handleEnroll}
                disabled={submitting || selectedIds.size === 0}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold rounded-xl shadow-md shadow-emerald-200 flex items-center gap-2 transition-all disabled:opacity-50"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    <span>משבץ...</span>
                  </>
                ) : (
                  <>
                    <UserPlus size={16} />
                    <span>שבץ {selectedIds.size} תלמידים לכיתת {targetClass.name}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};
